import {useAtomValue} from 'jotai';
import {useMemo} from 'react';
import styled from 'styled-components';
import {CheckIconComponent} from '@/components/Icons';
import {Typography} from '@/components/Typography';
import {MultiPolygonCoords} from '@/map/@types';
import {zoomLevelAtom} from '@/map/atom';
import {CoordType} from '@/map/enums';
import {Marker} from '@/map/modules';
import {getGeometryCenterPoint} from '@/map/utils';
import {interceptProps} from '@/styled-components';
import {palette} from '@/styles';

interface IStyleOptions {
  markerColor?: string;
  textColor?: string;
}

interface IProps extends IStyleOptions {
  map: naver.maps.Map;
  geometry: {
    type: CoordType.Polygon | CoordType.MultiPolygon;
    coordinates: MultiPolygonCoords;
  };
  text?: string;
  visibleZoomLevel?: number[];
  highlight?: boolean;
}

export const PolygonCenterTextMarker = ({
  map,
  geometry,
  text,
  visibleZoomLevel,
  highlight,
  ...styleOptions
}: IProps) => {
  const center = useMemo(
    () => getGeometryCenterPoint(geometry.coordinates),
    [geometry]
  );
  const zoomLevel = useAtomValue(zoomLevelAtom);
  const visible =
    !visibleZoomLevel?.length || visibleZoomLevel.includes(zoomLevel);

  if (!map || !center || !text?.length || !visible) {
    return null;
  }

  const textColor = styleOptions?.textColor ?? palette.white;

  return (
    <Marker
      map={map}
      position={center}
      clickable={false}
      draggable={false}
      zIndex={highlight ? 1 : 0}
    >
      <Root {...styleOptions} textColor={textColor}>
        {highlight && <Check color={textColor} />}
        <Typography variant="medium10" align="center">
          {text}
        </Typography>
      </Root>
    </Marker>
  );
};

const Check = styled(CheckIconComponent)`
  margin-right: 2px;
  width: 16px;
  height: 16px;
`;

const Root = styled('div').withConfig({
  shouldForwardProp: interceptProps(['markerColor', 'textColor']),
})<IStyleOptions>`
  ${({markerColor = palette.blue100, textColor}) => `
    background-color: ${markerColor};
    color: ${textColor};
  `}
  height: 24px;
  padding: 0 4px;
  border-radius: 3px;
  transform: translate(-50%, -50%);
  white-space: nowrap;
  display: flex;
  align-items: center;
  display: flex;
`;
